import React, { useState, useEffect } from 'react';
import { StarIcon } from '@heroicons/react/24/outline';

interface Gallery {
  url: string;
  publicId?: string;
  altText?: string;
}

interface FeaturedProjectAdCardProps {
  project: {
    _id: string;
    title: string;
    description?: string;
    image: string;
    gallery?: Gallery[];
    category: string;
    authorName: string;
    url: string;
  };
  variant?: 'compact';
}

const CATEGORY_COLORS: Record<string, string> = {
  web: '#3B82F6',
  mobile: '#3B82F6',
  ai: '#3B82F6',
  tool: '#3B82F6',
  game: '#3B82F6',
  other: '#3B82F6',
};

const FeaturedProjectAdCard: React.FC<FeaturedProjectAdCardProps> = ({ project }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Build gallery: primary image + gallery items
  const allImages = [project.image];
  if (project.gallery && Array.isArray(project.gallery)) {
    allImages.push(...project.gallery.map((g) => g.url));
  }

  // Auto-rotate images every 4 seconds if there are multiple
  useEffect(() => {
    if (allImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [allImages.length]);

  const catColor = CATEGORY_COLORS[project.category] || CATEGORY_COLORS.other;

  // Beautiful horizontal banner layout
  return (
    <div
      className="mx-auto my-6 overflow-hidden relative flex flex-col sm:flex-row w-full sm:w-[95%] md:w-[92%] lg:w-[90%]"
      style={{
        minHeight: '180px',
        background: 'var(--surface-1)',
        border: `1px solid var(--border-default)`,
        borderRadius: 'var(--r-md)',
        transition: 'all 200ms ease',
        animation: 'slideIn 200ms cubic-bezier(0.22, 1, 0.36, 1)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Image — Left side, larger on desktop */}
      <div
        className="w-full sm:w-[200px] lg:w-[280px]"
        style={{
          flexShrink: 0,
          height: '180px',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${catColor}15 0%, ${catColor}08 100%)`,
          position: 'relative',
          borderRight: 'none',
          borderBottom: `1px solid var(--border-default)`,
        }}
      >
        <img
          src={allImages[currentImageIndex]}
          alt={project.title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 400ms ease, transform 300ms ease',
            opacity: 1,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        />

        {/* Featured badge — floating on image */}
        <div
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: 'var(--r-xs)',
            fontSize: '10px',
            fontWeight: 700,
            zIndex: 20,
          }}
        >
          <StarIcon style={{ width: 11, height: 11 }} />
          Destacado
        </div>

        {/* Image indicator dots */}
        {allImages.length > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: 6,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 4,
              zIndex: 10,
            }}
          >
            {allImages.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background:
                    idx === currentImageIndex
                      ? 'rgba(255, 255, 255, 0.9)'
                      : 'rgba(255, 255, 255, 0.3)',
                  transition: 'all 200ms ease',
                  cursor: 'pointer',
                }}
                onClick={() => setCurrentImageIndex(idx)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content — Right side, flex-1 */}
      <div
        className="flex flex-col justify-between flex-1 p-4 sm:p-5 lg:p-6"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minWidth: 0,
        }}
      >
        {/* Top: Title + Meta */}
        <div style={{ marginBottom: 12, minWidth: 0 }}>
          <h3
            style={{
              fontSize: 'clamp(14px, 2vw, 18px)',
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1.4,
              margin: 0,
              marginBottom: 8,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical' as any,
              overflow: 'hidden',
            }}
          >
            {project.title}
          </h3>

          {project.description && (
            <p
              style={{
                fontSize: 'clamp(12px, 1.2vw, 14px)',
                color: 'var(--text-secondary)',
                lineHeight: 1.5,
                margin: 0,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical' as any,
                overflow: 'hidden',
              }}
            >
              {project.description}
            </p>
          )}
        </div>

        {/* Bottom: Badge + Author + Button */}
        <div className="flex items-center justify-between flex-wrap gap-3 sm:gap-4" style={{ alignItems: 'center' }}>
          <div className="flex items-center gap-3 flex-wrap" style={{ minWidth: 0 }}>
            {/* Category badge */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: catColor,
                fontSize: 'clamp(9px, 1vw, 11px)',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                padding: '4px 10px',
                borderRadius: 'var(--r-xs)',
                border: `1px solid ${catColor}40`,
                background: `${catColor}10`,
                whiteSpace: 'nowrap',
              }}
            >
              {project.category}
            </div>

            {/* Author */}
            <span
              style={{
                fontSize: 'clamp(11px, 1.1vw, 13px)',
                color: 'var(--text-secondary)',
                opacity: 0.7,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              by <strong style={{ color: 'var(--text-primary)' }}>{project.authorName}</strong>
            </span>
          </div>

          {/* Save Project Button */}
          <button
            onClick={() => window.open(project.url, '_blank')}
            style={{
              padding: 'clamp(8px, 0.7vw, 11px) clamp(14px, 1.5vw, 18px)',
              background: '#3b82f6',
              color: '#ffffff',
              border: '1.5px solid #3B82F6',
              borderRadius: 'var(--r-sm)',
              fontSize: 'clamp(11px, 1vw, 13px)',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 150ms ease',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              e.currentTarget.style.background = '#3B82F6';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.2)';
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#3B82F6';
            }}
          >
            <StarIcon style={{ width: 14, height: 14 }} />
            Save Project
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeaturedProjectAdCard;
